import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useBoards, BoardFormDialog } from '@/features/boards/public';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';

export default function ProjectBoardPage() {
  const { t } = useTranslation('tasks');
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const { boards, isLoading } = useBoards(projectId!);

  useEffect(() => {
    if (isLoading || boards.length === 0) return;
    const first = [...boards].sort((a, b) => a.order - b.order)[0];
    if (first.id) {
      navigate(`/${orgId}/projects/${projectId}/boards/${first.id}`, { replace: true });
    }
  }, [isLoading, boards, navigate, orgId, projectId]);

  if (isLoading || boards.length > 0) return null;

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div
        className="flex h-full flex-col items-center justify-center gap-4 p-8"
        data-testid="projects-board-page"
      >
        <LayoutGrid className="size-10 text-muted-foreground/40" />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">{t('boardView.emptyBoards')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('boardView.emptyBoardsDescription')}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="boards-create-button">
          {t('boardView.createFirstBoard')}
        </Button>

        <BoardFormDialog open={createOpen} onOpenChange={setCreateOpen} projectId={projectId!} />
      </div>
    </FeatureErrorBoundary>
  );
}
